// Publicar un lanzamiento en Mercado Libre, lo que el front arma sin red
// (ADR-2026-09-30-la-ficha-de-mercado-libre, fase 6 del plan del 30/9). El backend crea una
// publicación por color × talle (atlas-backend, ADR-2026-09-30-crear-publicaciones-en-mercadolibre).

import type { ProductoDeLanzamiento } from "../pages/lanzamientos/lanzamientosApi";

export interface AtributoConfirmado {
  id: string;
  value_id?: string | null;
  value_name?: string | null;
}

export interface AtributoQuePide {
  id: string;
  nombre: string;
  opciones: Array<{ id: string; nombre: string }>;
  obligatorio: boolean;
}

/** Lo que pide Mercado Libre y Tiendanube no: vive en `lanzamientos_productos.mercadolibre`. */
export interface DatosMlDeFicha {
  categoria_id: string;
  categoria_nombre?: string | null;
  /** "MLA-T_SHIRTS": lo necesita la guía de talles. */
  dominio_id?: string | null;
  /** Los que pide la categoría (se guardan para saber, sin red, si falta alguno). */
  atributos_obligatorios?: string[];
  atributos: AtributoConfirmado[];
  pide_guia_de_talles?: boolean;
  /** La categoría exige el color (toda la ropa): sin la variante Color, Mercado Libre lo rechaza. */
  pide_color?: boolean;
  guia_talles_id?: string | null;
  guia_nombre?: string | null;
  filas_por_talle?: Record<string, string>;
  tipo_publicacion?: "gold_special" | "gold_pro";
  garantia?: { tipo: string; tiempo?: string | null } | null;
}

export const TIPOS_DE_PUBLICACION: ReadonlyArray<{ id: "gold_special" | "gold_pro"; nombre: string; nota: string }> = [
  { id: "gold_special", nombre: "Clásica", nota: "Sin cuotas sin interés. Mercado Libre cobra menos por venta." },
  { id: "gold_pro", nombre: "Premium", nota: "Con cuotas sin interés y más exposición. Mercado Libre cobra más por venta." },
];

export const GARANTIA_POR_DEFECTO = { tipo: "Garantía del vendedor", tiempo: "30 días" };

/**
 * `pide_color` de la respuesta del backend. Un backend anterior a fix/ml-pide-color no lo manda: queda
 * `undefined` ("no se sabe"), no `false`, para que la ficha lo complete cuando llegue (review #189).
 */
export function pideColorDeLaRespuesta(v: unknown): boolean | undefined {
  return typeof v === "boolean" ? v : undefined;
}

/**
 * Qué guardar en la ficha al abrirla: el `pide_color` que dice hoy el backend, si difiere del
 * guardado (ficha armada antes, o con un backend que no lo mandaba). null = nada que cambiar.
 */
export function pideColorAActualizar(fresco: boolean | undefined, guardado: boolean | undefined): boolean | null {
  if (fresco === undefined || fresco === guardado) return null;
  return fresco;
}

const ES_COLOR = new Set(["color", "colores", "colour"]);
const ES_TALLE = new Set(["talle", "talles", "talla", "tallas", "size", "tamano", "medida"]);
const plano = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

export function indicesDeColorYTalle(atributos: readonly string[]): { color: number; talle: number } {
  const n = atributos.map(plano);
  return { color: n.findIndex((a) => ES_COLOR.has(a)), talle: n.findIndex((a) => ES_TALLE.has(a)) };
}

/** Un número de la ficha (viene como texto: "15000", "15000.5", "15000,5"). */
export function aNumero(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const t = v.includes(",") && !v.includes(".") ? v.replace(",", ".") : v;
  const n = Number(t.trim());
  return Number.isFinite(n) ? n : null;
}

/** Los talles del producto, en el orden de sus variantes. */
export function tallesDe(p: ProductoDeLanzamiento): string[] {
  const { talle } = indicesDeColorYTalle(p.atributos);
  if (talle < 0) return [];
  return [...new Set(p.variantes.map((v) => (v.valores[talle] ?? "").trim()).filter(Boolean))];
}

/** La ficha que se manda, en el formato del backend. */
export function fichaParaMl(p: ProductoDeLanzamiento) {
  return {
    nombre: p.nombre,
    descripcion: p.descripcion,
    precio: aNumero(p.precio),
    stock: p.stock,
    sku: p.sku,
    marca: p.marca,
    codigo_barras: p.codigoBarras,
    peso: aNumero(p.peso),
    alto: aNumero(p.alto),
    ancho: aNumero(p.ancho),
    profundidad: aNumero(p.profundidad),
    atributos: p.atributos,
    variantes: [...p.variantes]
      .sort((a, b) => a.orden - b.orden)
      .map((v) => ({
        valores: v.valores,
        precio: p.precioPorVariante ? aNumero(v.precio) : null,
        stock: aNumero(v.stock),
        sku: v.sku,
        codigo_barras: p.precioPorVariante ? v.codigoBarras : null,
      })),
  };
}

/**
 * Las fotos, cada una con el color de la variante a la que está asignada (null = foto general). Así
 * cada color de Mercado Libre lleva las suyas; los colores sin fotos propias usan las generales.
 */
export function fotosParaMl(p: ProductoDeLanzamiento): Array<{ archivo: string; color: string | null }> {
  const { color } = indicesDeColorYTalle(p.atributos);
  const colorDe = new Map(p.variantes.map((v) => [v.id, color >= 0 ? (v.valores[color] ?? "").trim() || null : null]));
  return [...p.imagenes]
    .sort((a, b) => a.orden - b.orden)
    .map((i) => ({ archivo: i.fileName, color: i.lanzamientoVarianteId ? (colorDe.get(i.lanzamientoVarianteId) ?? null) : null }));
}

/**
 * Lo que le falta a un producto para Mercado Libre, en palabras de la persona. null = listo para
 * "Revisar con Mercado Libre" (que dice el resto).
 */
export function faltaParaMl(p: ProductoDeLanzamiento, datos: DatosMlDeFicha | null | undefined): string | null {
  if (!datos?.categoria_id) return "falta elegir la categoría de Mercado Libre";
  const confirmados = new Set(datos.atributos.filter((a) => (a.value_name ?? "").trim() || a.value_id).map((a) => a.id));
  // El modelo y el fabricante no frenan: si quedan vacíos, los pone Atlas (el nombre y la marca).
  const falta = (datos.atributos_obligatorios ?? []).filter((id) => !LOS_PONE_ATLAS.has(id) && !confirmados.has(id));
  if (falta.length > 0) return "faltan datos que pide la categoría de Mercado Libre";
  // Atlas manda el color desde la variante Color; si no hay, Mercado Libre lo rechaza y recién
  // "Revisar" lo decía (prueba en vivo del 16/10).
  if (datos.pide_color && indicesDeColorYTalle(p.atributos).color < 0) {
    return "esta categoría de Mercado Libre pide el color: sumá la variante Color, aunque sea uno solo";
  }
  const talles = tallesDe(p);
  // Una categoría de ropa pide la guía de talles aunque el producto no tenga talles (Mercado Libre lo
  // rechaza con "Attribute [SIZE_GRID_ID] is missing"; prueba del 2/10).
  if (datos.pide_guia_de_talles && talles.length === 0) {
    return "esta categoría de Mercado Libre pide talles: sumá la variante Talle (S, M, L…) y su guía de talles";
  }
  if (datos.pide_guia_de_talles && talles.length > 0) {
    if (!datos.guia_talles_id) return "falta elegir la guía de talles de Mercado Libre";
    const sinFila = talles.filter((t) => !datos.filas_por_talle?.[t]);
    if (sinFila.length > 0) return `la guía de talles no tiene ${sinFila.length === 1 ? "el talle" : "los talles"} ${sinFila.join(", ")}`;
  }
  if (p.stock === null && p.variantes.every((v) => aNumero(v.stock) === null)) return "falta el stock";
  return null;
}

/** Los datos obligatorios que, si quedan vacíos, completa Atlas: el modelo (con el nombre) y el fabricante (con la marca). */
const LOS_PONE_ATLAS = new Set(["MODEL", "MANUFACTURER"]);

/** El pedido de un producto para `/v1/mercadolibre/publicar` (y, sin los ids, para validar). */
export function productoParaMl(p: ProductoDeLanzamiento, datos: DatosMlDeFicha) {
  return {
    lanzamiento_producto_id: p.id,
    producto_id: p.productoId,
    ficha: fichaParaMl(p),
    ml: {
      categoria_id: datos.categoria_id,
      atributos: conFabricante(datos, p.marca),
      // El backend completa lo que pone Atlas aunque la ficha no lo tenga (salvavidas).
      obligatorios: datos.atributos_obligatorios ?? [],
      guia_talles_id: datos.guia_talles_id ?? null,
      filas_por_talle: datos.filas_por_talle ?? {},
      tipo_publicacion: datos.tipo_publicacion ?? "gold_special",
      garantia: datos.garantia ?? GARANTIA_POR_DEFECTO,
    },
    fotos: fotosParaMl(p),
  };
}

/** Los atributos confirmados, y el fabricante con la marca si la categoría lo pide y quedó vacío. */
function conFabricante(datos: DatosMlDeFicha, marca: string | null | undefined): AtributoConfirmado[] {
  const confirmados = datos.atributos.filter((a) => (a.value_name ?? "").trim() || a.value_id);
  const pide = (datos.atributos_obligatorios ?? []).includes("MANUFACTURER");
  if (!pide || confirmados.some((a) => a.id === "MANUFACTURER") || !(marca ?? "").trim()) return confirmados;
  return [...confirmados, { id: "MANUFACTURER", value_name: marca!.trim() }];
}
